/**
 * Histogram renderer (blob `renderHistogram`). Bars from the
 * server bin edges; opacity steps down for overlays. Tooltip shows `[lo,hi)·mid`.
 */

import uPlot from 'uplot';
import { plotState } from '../../../state/plot-state.ts';
import { seriesColorAt } from '../plot-constants.ts';
import { withAlpha } from './format.ts';
import { themeColors, chartContainerSize } from './chart-dom.ts';
import {
  applyHoverFocus,
  buildLegendOpts,
  buildXAxis,
  buildYAxis,
  rebuildUplot,
} from './uplot-helpers.ts';
import { updateTooltip } from './tooltip.ts';
import type { HistogramResponse } from '../../../api/types.ts';

export function renderHistogram(data: HistogramResponse): void {
  const c = themeColors();
  const s = plotState.value;
  const edges = data.edges;
  const centers = edges.slice(0, -1).map((e, i) => (e + edges[i + 1]!) / 2);

  const uData: uPlot.AlignedData = [centers];
  const seriesOpts: uPlot.Series[] = [{}];
  const barsPaths = uPlot.paths?.bars ? uPlot.paths.bars({ size: [0.9], align: 0 }) : undefined;
  data.series.forEach((ser, i) => {
    const color = seriesColorAt(s, ser.name, i);
    const opt: uPlot.Series = {
      label: ser.name,
      scale: 'y',
      stroke: color,
      fill: withAlpha(color, data.series.length > 1 ? 0.45 : 0.75),
      width: 1,
      points: { show: false },
    };
    if (barsPaths) opt.paths = barsPaths;
    seriesOpts.push(opt);
    uData.push(ser.counts.map((v) => (v == null ? null : Number(v))));
  });

  const opts: uPlot.Options = {
    ...chartContainerSize(),
    scales: { x: { time: false }, y: {} },
    axes: [
      buildXAxis(c, false, data.columns && data.columns.length === 1 ? data.columns[0]! : 'Value'),
      buildYAxis(c, 'y', 'left', 'Count'),
    ],
    series: seriesOpts,
    cursor: {
      x: true,
      y: false,
      drag: { x: false, y: false },
    },
    legend: buildLegendOpts(data.series.length),
    hooks: {
      setCursor: [(u): void => updateTooltip(u, data, seriesOpts, { isHist: true, edges })],
    },
  };

  applyHoverFocus(opts);
  rebuildUplot(uData, opts);
}
